import { DOCUMENT, NgClass } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideBadgeCheck,
  lucideCircleAlert,
  lucideDownload,
  lucideExternalLink,
  lucideFileText,
  lucideGlobe,
  lucideLaptop,
  lucidePlus,
  lucideRefreshCw,
  lucideStar,
  lucideUsers,
} from '@ng-icons/lucide';
import { BrnSeparatorComponent } from '@spartan-ng/brain/separator';
import { HlmBadgeDirective } from '@spartan-ng/ui-badge-helm';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm';
import { HlmSeparatorDirective } from '@spartan-ng/ui-separator-helm';
import { environment } from '../../../environments/environment';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { NewsCardComponent } from '../../components/news-card/news-card.component';
import { NewsletterSubscribeComponent } from '../../components/newsletter-subscribe/newsletter-subscribe.component';
import { McpClient, McpServer } from '../../models/mcp.model'; // Added McpClient, McpServer
import { NewsArticle } from '../../models/news-article.model'; // Added NewsArticle
import { McpService } from '../../services/mcp.service'; // Added McpService
import { NewsService } from '../../services/news.service'; // Added NewsService
import { SEOService } from '../../services/seo.service'; // Added SEOService
import { StructuredDataService } from '../../services/structured-data.service'; // Added StructuredDataService
import { DataFeedElementSchema, Schema } from '../../types/structured-data.interfaces';
import { formatNumberShorthand } from '../../utils/number-formatting.utils';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    NgClass, // Added NgClass here as it's used in template
    RouterLink, // Added RouterLink
    NewsCardComponent,
    LoadingSpinnerComponent,
    ErrorMessageComponent,
    HlmButtonDirective,
    HlmIconDirective,
    NgIcon,
    HlmBadgeDirective,
    HlmSeparatorDirective,
    BrnSeparatorComponent,
    NewsletterSubscribeComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  providers: [
    provideIcons({
      lucidePlus,
      lucideRefreshCw,
      lucideCircleAlert,
      lucideFileText,
      lucideArrowRight,
      lucideGlobe,
      lucideLaptop,
      lucideStar,
      lucideBadgeCheck,
      lucideUsers,
      lucideDownload,
      lucideExternalLink,
    }),
  ],
})
export class HomeComponent implements OnInit {
  error: string | null = null;
  private structuredDataAdded = false; // Added flag

  // MCP servers
  servers: McpServer[] = [];
  loadingServers = false;
  serverError: string | null = null;

  // MCP clients
  clients: McpClient[] = [];
  loadingClients = false;
  clientError: string | null = null;

  // News articles
  articles: NewsArticle[] = [];
  page = 1;
  limit = 9; // Display 9 news cards (3x3 grid)
  loading = false;

  private document = inject(DOCUMENT);
  private newsService = inject(NewsService);
  private mcpService = inject(McpService);
  private seoService = inject(SEOService);
  private structuredDataService = inject(StructuredDataService);

  ngOnInit() {
    this.setSeoMetadata();
    this.loadArticles();
    this.loadMcpServers();
    this.loadMcpClients();
  }

  private setSeoMetadata() {
    const canonicalUrl = `${environment.canonicalOrigin}/`;

    this.seoService.updateTags({
      title: 'Summerry - AI News, MCP Servers & Clients Hub',
      description:
        'Discover the latest AI news, explore Model Context Protocol (MCP) servers and clients, and stay updated with cutting-edge developments in artificial intelligence and machine learning technology.',
      keywords:
        'AI news, artificial intelligence, MCP, Model Context Protocol, MCP servers, MCP clients, AI developments, machine learning, tech news, AI tools, AI ecosystem, newsletter',
      type: 'website',
      url: canonicalUrl, // Explicitly set the canonical URL
    });
  }

  loadArticles() {
    this.loading = true;
    this.error = null;
    this.page = 1;
    this.articles = [];
    this.newsService.getNews(this.page, this.limit).subscribe({
      next: response => {
        // Assuming 'response.items' is the correct path for articles.
        // Adjust if your actual response structure for news is different.
        this.articles = response.items || [];
        this.loading = false;
        // if (response.totalPages) { this.totalPages = response.totalPages; }
        this.checkAndAddStructuredData(); // Call to check and add structured data
      },
      error: err => {
        this.loading = false;
        console.error('Error loading news:', err);
        this.error = 'Failed to load news articles. Please try again later.';
        // Optionally, still try to add structured data if partial data is acceptable,
        // but current checkAndAddStructuredData requires all data.
        // this.checkAndAddStructuredData();
      },
    });
  }

  loadMcpServers() {
    this.loadingServers = true;
    this.serverError = null;

    // Get top 9 MCP servers sorted by stars
    this.mcpService.getMcpServers(1, 9, 'stars').subscribe({
      next: response => {
        this.servers = response.items;
        this.loadingServers = false;
        this.checkAndAddStructuredData(); // Call to check and add structured data
      },
      error: err => {
        this.serverError = 'Failed to load MCP servers. Please try again later.';
        this.loadingServers = false;
        console.error('Error loading MCP servers:', err);
      },
    });
  }

  loadMcpClients() {
    this.loadingClients = true;
    this.clientError = null;

    // Get top 9 MCP clients sorted by stars
    this.mcpService.getMcpClients(1, 9, 'stars').subscribe({
      next: response => {
        this.clients = response.items;
        this.loadingClients = false;
        this.checkAndAddStructuredData(); // Call to check and add structured data
      },
      error: err => {
        this.clientError = 'Failed to load MCP clients. Please try again later.';
        this.loadingClients = false;
        console.error('Error loading MCP clients:', err);
      },
    });
  }

  // New method to add structured data for the home page
  private addHomePageStructuredData() {
    const origin = environment.canonicalOrigin; // Use document.location.origin
    const newsItemList: DataFeedElementSchema[] = this.articles.slice(0, 9).map((article, index) => ({
      '@type': 'DataFeedItem',
      position: index + 1,
      item: {
        '@type': 'NewsArticle',
        '@id': `${origin}/news/${article.slug}`,
        headline: article.title,
        url: `${origin}/news/${article.slug}`,
        image: `${origin}/assets/og/black-og-image-1x.png`,
        author: {
          '@type': 'Organization',
          '@id': `${origin}/#organization`,
        },
      },
    }));

    const serverItemList: Schema[] = this.servers.slice(0, 12).map((server, index) => {
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'SoftwareSourceCode',
          '@id': server.url,
          name: server.name,
          url: server.url,
          codeRepository: server.url,
        },
      };
    });

    const clientItemList: Schema[] = this.clients.slice(0, 12).map((client, index) => {
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'SoftwareSourceCode',
          '@id': client.urls[0],
          name: client.name,
          url: client.urls[0],
          codeRepository: client.urls[0],
        },
      };
    });

    const hasPartItems: Schema[] = [
      {
        '@type': 'DataFeed',
        name: 'Latest News Articles',
        dataFeedElement: newsItemList,
      },
      {
        '@type': 'ItemList',
        name: 'Featured MCP Servers',
        itemListElement: serverItemList,
      },
      {
        '@type': 'ItemList',
        name: 'Featured MCP Clients',
        itemListElement: clientItemList,
      },
      {
        '@type': 'WebPageElement',
        name: 'Subscribe to Newsletter',
        potentialAction: {
          '@type': 'SubscribeAction',
          target: `${environment.apiUrl}/newsletter/subscribe`, // Endpoint for the actual subscription
        },
      },
    ];

    this.structuredDataService.addWebPage({
      name: 'Summerry - AI-Powered News Summaries & MCP Tools',
      description:
        'Stay informed with Summerry: Your source for AI-driven news summaries and a comprehensive suite of MCP server and client tools. Explore the future of information.',
      breadcrumbItems: [{ name: 'Home' }], // Provide a breadcrumb item for the home page
      hasPart: hasPartItems,
    });
  }

  // New private method to check conditions and add structured data
  private checkAndAddStructuredData() {
    if (
      this.articles &&
      this.articles.length > 0 &&
      this.servers &&
      this.servers.length > 0 &&
      this.clients &&
      this.clients.length > 0 &&
      !this.structuredDataAdded
    ) {
      this.addHomePageStructuredData();
      this.structuredDataAdded = true;
    }
  }

  // Helper function to find a GitHub URL in an array of URLs
  findGithubUrl(urls: string[] | undefined): string | undefined {
    if (!urls) {
      return undefined;
    }
    return urls.find(url => url.includes('github.com'));
  }

  // Helper function to extract domain name from URL
  getDomainFromUrl(url: string | undefined): string {
    if (!url) {
      return 'N/A';
    }
    try {
      const hostname = new URL(url).hostname;
      // Remove www. if present
      return hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    } catch {
      return 'Invalid URL';
    }
  }

  // Helper function to format numbers (e.g. star counts)
  formatNumber(value?: number, fallback = '0') {
    if (value === undefined || value === null) {
      return fallback;
    }
    return formatNumberShorthand(value);
  }

  // Helper function to extract owner name from repo URL
  getRepoOwner(url: string) {
    if (!url) return 'Unknown';
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname === 'github.com') {
        const pathParts = parsedUrl.pathname.split('/').filter(part => part.length > 0);
        return pathParts[0] || 'Unknown';
      }
    } catch {
      // URL parsing failed
    }
    return 'Unknown';
  }
}
